const DEFAULT_NESTED_KEYS = ['answer'];

/**
 * Flattens transition form values before submit.
 *
 * - Object values with a `vendors` array: extract first vendor (single-vendor fields).
 * - Object values with a recognized nested key (e.g. `answer`): extract that nested value.
 * - Keys in `expandObjectKeys`: spread object fields into the top-level result.
 * - Everything else passes through unchanged.
 */
export function flattenTransitionSubmitData(data, nestedKeys = DEFAULT_NESTED_KEYS, expandObjectKeys = []) {
  return Object.entries(data || {}).reduce((acc, [key, value]) => {
    // Single-vendor fields are stored as { vendors: [vendor] } — unwrap to just the vendor.
    if (value && typeof value === 'object' && Array.isArray(value.vendors)) {
      acc[key] = value.vendors[0] ?? null;
      return acc;
    }

    const nestedKey =
      value && typeof value === 'object' && !Array.isArray(value)
        ? nestedKeys.find((k) => value[k] !== undefined)
        : undefined;

    // Some keys (e.g. "bedCount") store grouped fields as an object — spread them into root.
    if (!nestedKey && expandObjectKeys.includes(key) && value && typeof value === 'object' && !Array.isArray(value)) {
      return { ...acc, ...value };
    }

    acc[key] = nestedKey ? value[nestedKey] : value;
    return acc;
  }, {});
}

/**
 * Builds the Vendors form payload expected by SaveVendorsForm.
 *
 * Field value shapes differ by whether a field allows multiple vendors:
 *
 *   Single vendor:   { vendors: [vendorObject] }          (FormVendorField)
 *   Multi vendor:    [{ vendors: [vendorObject] }, ...]   (FormVendorFieldWithAddMore)
 *
 * Both are normalized into an array of backend row objects:
 *   [{ vendor, contractFiles, invoiceFiles }, ...]
 */
export function buildVendorsFormSubmitData(allData, vendorFieldsConfig = []) {
  const vendorFields = vendorFieldsConfig.map((field) => {
    const { fieldName } = field.question;
    const fieldValue = allData?.[fieldName];
    const allowMultipleVendors = !!field.allowMultipleVendors;

    return {
      fieldName,
      vendors: extractVendorRows(fieldValue, allowMultipleVendors),
    };
  });

  return {
    transitionFacilityId: allData?.transitionFacilityId,
    vendorFields,
  };
}

/**
 * Extracts vendor rows from a field value into the backend row shape.
 *
 * Multi-vendor fields hold an array of row wrappers: [{ vendors: [vendor] }, ...]
 * Single-vendor fields hold one row wrapper:          { vendors: [vendor] }
 */
function extractVendorRows(fieldValue, allowMultipleVendors) {
  const rawVendors = allowMultipleVendors ? unwrapMultiVendorRows(fieldValue) : unwrapSingleVendorRow(fieldValue);

  return rawVendors.filter((vendor) => vendor !== null).map(toBackendVendorRow);
}

/** Unwraps a multi-vendor field: array of row wrappers → flat array of vendor objects. */
function unwrapMultiVendorRows(fieldValue) {
  if (!Array.isArray(fieldValue)) return [];

  return fieldValue.map((row) => (Array.isArray(row?.vendors) ? (row.vendors[0] ?? null) : (row ?? null)));
}

/** Unwraps a single-vendor field: { vendors: [vendor] } → [vendor]. */
function unwrapSingleVendorRow(fieldValue) {
  return Array.isArray(fieldValue?.vendors) ? fieldValue.vendors : [];
}

/** Converts a vendor object into the row shape the backend expects. */
function toBackendVendorRow(vendor) {
  return {
    vendor,
    contractFiles: vendor?.contractFiles ?? [],
    invoiceFiles: vendor?.invoiceFiles ?? [],
  };
}
